import React from 'react'

import LockIcon from '@mui/icons-material/Lock'
import { Button, InputAdornment, Typography } from '@mui/material'
import { Box } from '@mui/system'
import {
	BusinessTopicFiltered,
	BusinessTopicType,
} from 'constants/enums/TopicTypeEnum'
import { countWords } from 'utils/countWords'

import BusinessTopicChips from 'components/BusinessTopicChips'
import { BaseTextField } from 'components/Form'
import TopicRefinementSection from 'components/TopicRefinementSection'
import WarningMessage from 'components/WarningMessage/WarningMessage'

import { commonStyles } from 'styles/common.styles'

import { styles } from './Section1.styles'

export const TEST_ID = 'leap-frog-section1'

export type Props = {
	companyProblem: string
	createPrompt: () => void
	editable: boolean
	generateScenarioDisabled: () => boolean
	innovationCompany: string
	isLoading: boolean
	resetAllField: () => void
	setCompanyProblem: (val: string) => void
	setInnovationCompany: (val: string) => void
	setTopic: (val: BusinessTopicType) => void
	topic: BusinessTopicType
}

const Section1 = ({
	setTopic,
	topic,
	innovationCompany,
	setInnovationCompany,
	companyProblem,
	setCompanyProblem,
	generateScenarioDisabled,
	resetAllField,
	isLoading,
	createPrompt,
	editable,
}: Props) => {
	React.useEffect(() => {
		const storedCompany = localStorage.getItem('innovationCompany')
		const storedTopic = localStorage.getItem('innovationTopic')
		const storedProblem = localStorage.getItem('companyProblem')
		if (storedCompany) setInnovationCompany(storedCompany)
		if (
			storedTopic &&
			(Object.values(BusinessTopicFiltered) as string[]).includes(
				storedTopic
			)
		) {
			setTopic(storedTopic as BusinessTopicType)
		}
		if (storedProblem) setCompanyProblem(storedProblem)
	}, [setInnovationCompany, setCompanyProblem, setTopic])
	React.useEffect(() => {
		const debounce = setTimeout(() => {
			localStorage.setItem('innovationCompany', innovationCompany)
			if (topic) localStorage.setItem('innovationTopic', topic)
			localStorage.setItem('companyProblem', companyProblem)
		}, 200)
		return () => clearTimeout(debounce)
	}, [innovationCompany, topic, companyProblem])

	return (
		<>
			<Typography
				variant={'overline'}
				sx={{ ...commonStyles.header, ...styles.header }}
			>
				INNOVATION TOPIC
			</Typography>
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display={'flex'}
					flexDirection={'column'}
					rowGap={'3rem'}
					flexGrow="1"
					data-testid={`${TEST_ID}__company-input-container`}
				>
					<Typography
						color="black"
						sx={commonStyles.textOpaque}
						data-testid={`${TEST_ID}__company-brainstorm-prompt`}
					>
						What company are you brainstorming about today?
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{ style: styles.inputLabel }}
						variant="outlined"
						data-testid={`${TEST_ID}__company-name-input`}
						label="Enter company name here."
						value={innovationCompany}
						onChangeValue={(val) => setInnovationCompany(val)}
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiInputBase-input': {
								boxSizing: 'content-box',
								height: '100%',
							},
							...styles.textField,
						}}
					/>
					<Box
						display={'flex'}
						flexDirection={'column'}
						rowGap={'1rem'}
						flexGrow="1"
					>
						<Typography
							sx={commonStyles.header}
							data-testid={`${TEST_ID}__business-function-prompt`}
						>
							What business function do you want to focus on?
						</Typography>
						<BusinessTopicChips
							innovationTopic={topic}
							setInnovationTopic={setTopic}
							backgroundColor="#E4F4D8"
							editable={editable}
							topics={Object.values(BusinessTopicFiltered)}
						/>
					</Box>
					<Typography
						color="black"
						sx={commonStyles.textOpaque}
						data-testid={`${TEST_ID}__summarize-prompt`}
					>
						Enter the problem to solve by finishing this phrase: how
						might we create new ways to...
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-problem-input`}
						label="Finish the phrase: how might we create new ways to ..."
						onChangeValue={(val) => setCompanyProblem(val)}
						value={companyProblem}
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiInputBase-input': {
								boxSizing: 'content-box',
								height: '100%',
							},
							...styles.textField,
						}}
					/>
					<WarningMessage
						prompt={companyProblem}
						color="gameLeapFrog"
					/>
					<TopicRefinementSection
						backgroundColor={'#E4F4D8'}
						businessProblem={companyProblem}
						companyName={innovationCompany}
						dataTestId={TEST_ID}
						customGame={editable}
					/>
					<Box
						display="flex"
						sx={{
							flexDirection: {
								xs: 'column-reverse',
								sm: 'row',
							},
						}}
						justifyContent="flex-start"
						gap="2rem"
					>
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__create-prompt-button`}
							disabled={generateScenarioDisabled() || isLoading}
							onClick={createPrompt}
							aria-describedby={
								countWords(companyProblem) >= 15
									? 'topic-refinement-education'
									: undefined
							}
						>
							START IDEATING
						</Button>
						<Button
							disabled={!editable}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__reset-all-button`}
							onClick={resetAllField}
						>
							RESET ALL FIELDS
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	)
}

export default Section1
